import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { settingsService } from "services/settings/settings.service"

import { CioTenantConfig } from "types/api"

import { INITIAL_FORM } from "./constants"
import { EventNameFormFields, FormState, TemplateFormFields } from "./types"
import { buildPayload, configToForm } from "./utils"

export interface UseSettingsResult {
  form: FormState
  loadedConfig: CioTenantConfig | null
  loading: boolean
  saving: boolean
  deleting: boolean
  setField: <K extends keyof Pick<FormState, "enabled" | "siteId" | "apiKey" | "appApiKey" | "region" | "amountFormat">>(key: K, value: FormState[K]) => void
  setTemplate: (key: keyof TemplateFormFields, value: string) => void
  setEventName: (key: keyof EventNameFormFields, value: string) => void
  handleSave: () => Promise<void>
  handleDeleteCredentials: () => Promise<void>
  /** Reloads from the API — used after an external reset. */
  refetch: () => Promise<void>
}

export function useSettings(): UseSettingsResult {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [loadedConfig, setLoadedConfig] = useState<CioTenantConfig | null>(null)
  const [form, setForm] = useState<FormState>(INITIAL_FORM)

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    try {
      const config = await settingsService.getCioSettings()
      setLoadedConfig(config)
      if (config) setForm(configToForm(config))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  const setField = useCallback(
    <K extends keyof Pick<FormState, "enabled" | "siteId" | "apiKey" | "appApiKey" | "region" | "amountFormat">>(key: K, value: FormState[K]) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    []
  )

  const setTemplate = useCallback(
    (key: keyof TemplateFormFields, value: string) =>
      setForm((prev) => ({
        ...prev,
        transactionalTemplates: { ...prev.transactionalTemplates, [key]: value }
      })),
    []
  )

  const setEventName = useCallback(
    (key: keyof EventNameFormFields, value: string) =>
      setForm((prev) => ({
        ...prev,
        eventNames: { ...prev.eventNames, [key]: value }
      })),
    []
  )

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      const success = await settingsService.updateCioSettings(buildPayload(form, loadedConfig))
      if (success) {
        toast.success(t("settings.customerio.updated") as string)
        await fetchConfig()
      }
    } finally {
      setSaving(false)
    }
  }, [form, loadedConfig, fetchConfig, t])

  const handleDeleteCredentials = useCallback(async () => {
    setDeleting(true)
    try {
      const success = await settingsService.deleteCioCredentials()
      if (success) await fetchConfig()
    } finally {
      setDeleting(false)
    }
  }, [fetchConfig])

  return {
    form,
    loadedConfig,
    loading,
    saving,
    deleting,
    setField,
    setTemplate,
    setEventName,
    handleSave,
    handleDeleteCredentials,
    refetch: fetchConfig
  }
}
